/**
 * AD/Entra Phase 3 React Query hooks.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applyAdSyncAction,
  applyAdSyncActionsBatch,
  applyAdSyncActionsInChunks,
  computeAdSyncProposals,
  getAdSyncRun,
  getAdSyncTerritoryNeeds,
  listAdSyncProposals,
  listAdSyncRuns,
  rejectAdSyncAction,
  setAdSyncActionTerritory,
  triggerAdSyncFetch,
} from "../services/adSync.service";
import type { AdSyncProposalFilters } from "../types/adSync.types";

const KEYS = {
  runs: ["adSync", "runs"] as const,
  run: (id: string) => ["adSync", "run", id] as const,
  proposals: (runId: string, filters: AdSyncProposalFilters) =>
    ["adSync", "proposals", runId, filters] as const,
  proposalsBase: (runId: string) => ["adSync", "proposals", runId] as const,
};

export function useAdSyncRuns() {
  return useQuery({
    queryKey: KEYS.runs,
    queryFn: () => listAdSyncRuns(),
    refetchInterval: 30_000,
  });
}

export function useAdSyncRun(runId: string | undefined) {
  return useQuery({
    queryKey: runId ? KEYS.run(runId) : ["adSync", "run", "noop"],
    queryFn: () => getAdSyncRun(runId!),
    enabled: !!runId,
    refetchInterval: 15_000,
  });
}

export function useAdSyncProposals(
  runId: string | undefined,
  filters: AdSyncProposalFilters = {},
) {
  return useQuery({
    queryKey: runId
      ? KEYS.proposals(runId, filters)
      : ["adSync", "proposals", "noop"],
    queryFn: () => listAdSyncProposals(runId!, filters),
    enabled: !!runId,
  });
}

export function useTriggerAdSyncFetch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => triggerAdSyncFetch(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.runs });
    },
  });
}

export function useComputeAdSyncProposals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => computeAdSyncProposals(runId),
    onSuccess: (_data, runId) => {
      qc.invalidateQueries({ queryKey: KEYS.run(runId) });
      qc.invalidateQueries({ queryKey: KEYS.proposalsBase(runId) });
      qc.invalidateQueries({ queryKey: KEYS.runs });
    },
  });
}

export function useApplyAdSyncAction(runId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actionId: string) => applyAdSyncAction(actionId),
    onSuccess: () => {
      if (runId) {
        qc.invalidateQueries({ queryKey: KEYS.proposalsBase(runId) });
      }
    },
  });
}

export function useRejectAdSyncAction(runId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ actionId, reason }: { actionId: string; reason: string }) =>
      rejectAdSyncAction(actionId, reason),
    onSuccess: () => {
      if (runId) {
        qc.invalidateQueries({ queryKey: KEYS.proposalsBase(runId) });
      }
    },
  });
}

export function useApplyAdSyncActionsBatch(runId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actionIds: string[]) => applyAdSyncActionsBatch(actionIds),
    onSuccess: () => {
      if (runId) {
        qc.invalidateQueries({ queryKey: KEYS.proposalsBase(runId) });
      }
    },
  });
}

/**
 * Bulk apply for "Select all": many ids, chunked, in dependency order, through
 * the same apply_ad_sync_actions_batch RPC. Invalidates once at the end (not per
 * chunk) so the long list is not refetched mid-run.
 */
export function useApplyAdSyncActionsInChunks(runId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      actions,
      onProgress,
    }: {
      actions: Parameters<typeof applyAdSyncActionsInChunks>[0];
      onProgress?: (done: number, total: number) => void;
    }) => applyAdSyncActionsInChunks(actions, onProgress),
    onSettled: () => {
      if (runId) {
        qc.invalidateQueries({ queryKey: KEYS.proposalsBase(runId) });
      }
    },
  });
}

/** RM / TM proposals of a run with their territory (mig 1243). */
export function useAdSyncTerritoryNeeds(runId: string | undefined) {
  return useQuery({
    queryKey: ["adSync", "territory", runId],
    queryFn: () => getAdSyncTerritoryNeeds(runId as string),
    enabled: !!runId,
  });
}

export function useSetAdSyncActionTerritory(runId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { actionId: string; states: string[] }) =>
      setAdSyncActionTerritory(v.actionId, v.states),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["adSync", "territory", runId] });
      if (runId) {
        qc.invalidateQueries({ queryKey: KEYS.proposalsBase(runId) });
      }
    },
  });
}
